import React, { useRef, useEffect } from 'react'
import { MentionsInput, Mention } from 'react-mentions'
import './HPTTextarea.scss'

const HPTTextarea = ({
    data = [],
    value = '',
    onChange,
    placeholder = 'Type @ to mention a note template...',
    trigger = '@',
    rows = 5,
    className = '',
    disabled = false,
    name = 'hptTextarea'
}) => {
    const previousValueRef = useRef('')
    const inputRef = useRef(null)
    const cursorPositionRef = useRef(null)

    // Transform data to the format expected by react-mentions
    const mentionData = data
        .filter(item => !item.isDeleted) // Filter out deleted items
        .map(item => ({
            id: item.notesTemplateId,
            display: item.shortCode,
            notes: item.notes
        }))

    // Handle change event and replace mentions with actual notes
    const handleChange = (event, newValue, newPlainTextValue, mentions) => {
        // Check if a new mention was just added by comparing values
        const mentionPattern = /@\[([^\]]+)\]\(([^)]+)\)/g
        const previousMentions = (previousValueRef.current.match(mentionPattern) || [])
        const currentMentions = (newValue.match(mentionPattern) || [])
        
        if (currentMentions.length > previousMentions.length) {
            // A new mention was added, find it and replace it
            const newMentionMatch = currentMentions.find(m => !previousMentions.includes(m))
            
            if (newMentionMatch) {
                // Extract the ID from the mention
                const idMatch = newMentionMatch.match(/@\[[^\]]+\]\(([^)]+)\)/)
                if (idMatch && idMatch[1]) {
                    const mentionId = idMatch[1]
                    
                    // Find the corresponding note template
                    const noteTemplate = data.find(item => item.notesTemplateId === mentionId)
                    
                    if (noteTemplate && noteTemplate.notes) {
                        // Find the position of the mention in the string
                        const mentionIndex = newValue.indexOf(newMentionMatch)
                        
                        // Replace the mention markup with the actual notes text
                        const updatedValue = newValue.replace(newMentionMatch, noteTemplate.notes)
                        
                        // Calculate cursor position (end of inserted text + space if appendSpaceOnAdd is true)
                        const newCursorPos = mentionIndex + noteTemplate.notes.length + 1
                        cursorPositionRef.current = newCursorPos
                        
                        // Update the ref
                        previousValueRef.current = updatedValue
                        
                        // Call onChange with the updated value (plain text, no mentions)
                        if (onChange) {
                            onChange(event, updatedValue, updatedValue, [])
                        }
                        return
                    }
                }
            }
        }
        
        // Update the ref
        previousValueRef.current = newValue
        
        // Normal change
        if (onChange) {
            onChange(event, newValue, newPlainTextValue, mentions)
        }
    }
    
    // Set cursor position after value update
    useEffect(() => {
        if (cursorPositionRef.current !== null && inputRef.current) {
            const textarea = inputRef.current.querySelector('textarea')
            if (textarea) {
                setTimeout(() => {
                    textarea.focus()
                    textarea.setSelectionRange(cursorPositionRef.current, cursorPositionRef.current)
                    cursorPositionRef.current = null
                }, 0)
            }
        }
    }, [value])
    
    // Update ref when value changes externally
    useEffect(() => {
        previousValueRef.current = value
    }, [value])

    // Render suggestion
    const renderSuggestion = (suggestion, search, highlightedDisplay, index, focused) => {
        return (
            <div className={`mention-suggestion ${focused ? 'focused' : ''}`}>
                <div className="mention-shortcode">{suggestion.display}</div>
            </div>
        )
    }

    return (
        <div className={`hpt-textarea-wrapper ${className}`} ref={inputRef}>
            <MentionsInput
                value={value}
                onChange={handleChange}
                placeholder={placeholder}
                disabled={disabled}
                className="hpt-mentions-input"
                style={{
                    // control: {
                    //     fontWeight: 'normal',
                    // },
                    '&multiLine': {
                        control: {
                            minHeight: rows * 20,
                        },
                        highlighter: {
                            border: '1px solid transparent',
                        },
                        input: {
                            border: '1px solid #d9d9d9',
                            borderRadius: '4px',
                            outline: 'none',
                        },
                    },
                    suggestions: {
                        list: {
                            backgroundColor: 'white',
                            border: '1px solid rgba(0,0,0,0.15)',
                            fontSize: 14,
                            maxHeight: 300,
                            overflow: 'auto',
                        },
                        item: {
                            padding: '8px 12px',
                            borderBottom: '1px solid rgba(0,0,0,0.05)',
                            '&focused': {
                                backgroundColor: 'var(--primary-light)',
                            },
                        },
                    },
                }}
            >
                <Mention
                    trigger={trigger}
                    data={mentionData}
                    renderSuggestion={renderSuggestion}
                    markup="@[__display__](__id__)"
                    displayTransform={(id, display) => `@${display}`}
                    appendSpaceOnAdd={true}
                />
            </MentionsInput>
        </div>
    )
}

export default HPTTextarea
